import { useOnboarding, PRICING } from "./OnboardingContext";
import { Check, Sparkles, Calendar, Droplets, Monitor, QrCode, FileText, Clock, Info } from "lucide-react";

const StepIntro = () => {
  const { selectedPackage, nextStep } = useOnboarding();
  const pkg = PRICING.packages[selectedPackage];

  const getBenefits = () => {
    if (!pkg) return [];
    return [
      { icon: Droplets, label: `${pkg.flacons} flacons inclus`, value: `${pkg.mlPerFlacon}ML / flacon / trimestre` },
      { icon: Monitor, label: "Affichage digital", value: `Jusqu'à ${pkg.digitalDisplay}% du temps` },
      { icon: Calendar, label: "Modification écran", value: `Tous les ${pkg.screenModification} mois` },
      { icon: QrCode, label: "QR Code", value: pkg.qrCode ? "Inclus" : "Non inclus" },
      { icon: FileText, label: "Rapports", value: pkg.reportFrequency.charAt(0).toUpperCase() + pkg.reportFrequency.slice(1) },
      { icon: Sparkles, label: "Personnalisation logo", value: pkg.logoCustomization ? "Disponible" : "Non disponible" }
    ];
  };

  return (
    <div
      data-testid="step-intro"
      className="max-w-2xl mx-auto text-center py-12 animate-fade-in"
    >
      {/* Icon */}
      <div className="w-20 h-20 mx-auto mb-8 bg-[#0A0A0A] flex items-center justify-center">
        <Sparkles size={32} className="text-[#B89F7C]" strokeWidth={1.5} />
      </div>

      {/* Title */}
      <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#0A0A0A] mb-6">
        Réservez votre dispositif
      </h1>

      {/* Subtitle */}
      <p className="font-body text-lg text-[#525252] mb-8 max-w-lg mx-auto">
        En quelques étapes, configurez votre solution de diffusion de parfums
        et réservez votre présence dans des lieux premium.
      </p>

      {/* Pre-order Banner */}
      <div className="bg-[#0A0A0A] text-white p-6 mb-10 text-left">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-[#B89F7C]/20 flex items-center justify-center shrink-0">
            <Clock size={20} className="text-[#B89F7C]" />
          </div>
          <div>
            <h3 className="font-heading text-lg mb-2">Offre en précommande</h3>
            <p className="font-body text-sm text-white/80 leading-relaxed">
              Le dispositif est actuellement proposé en précommande. La mise en service intervient dans un 
              <span className="text-[#B89F7C] font-medium"> délai estimé de 1 à 4 mois </span>
              à compter de la validation du projet et selon les disponibilités opérationnelles de notre réseau partenaire.
            </p>
          </div>
        </div>
      </div>

      {/* Selected Package Card */}
      {pkg && (
        <div className="bg-white border border-[#E5E5E5] p-8 mb-10 text-left">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
                Offre sélectionnée
              </p>
              <h2 className="font-heading text-2xl text-[#0A0A0A] mb-2">
                {pkg.name}
              </h2>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-heading text-[#0A0A0A]">{pkg.basePrice}€</span>
                <span className="text-sm font-body text-[#525252]">HT / mois</span>
              </div>
            </div>
            <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center">
              <Check size={20} className="text-white" />
            </div>
          </div>

          {/* Engagement */}
          <div className="bg-[#F9F6F0] p-4 mb-6">
            <p className="font-body text-sm text-[#525252]">
              <span className="font-medium text-[#0A0A0A]">Engagement minimum :</span> {pkg.engagement} mois
            </p>
            <p className="font-body text-sm text-[#525252]">
              <span className="font-medium text-[#0A0A0A]">Frais de réservation et mise en service :</span> {pkg.setupFee}€ HT
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {getBenefits().map((benefit) => (
              <div key={benefit.label} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#F9F6F0] flex items-center justify-center shrink-0">
                  <benefit.icon size={16} className="text-[#B89F7C]" />
                </div>
                <div>
                  <p className="font-body text-sm font-medium text-[#0A0A0A]">{benefit.label}</p>
                  <p className="font-body text-xs text-[#525252]">{benefit.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What's next */}
      <div className="text-left mb-10">
        <h3 className="font-body text-sm tracking-[0.15em] uppercase text-[#525252] mb-4">
          Votre parcours de réservation
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { step: 1, text: "Niveau de diffusion" },
            { step: 2, text: "Configuration de votre dispositif" },
            { step: 3, text: "Coordonnées de votre entreprise" },
            { step: 4, text: "Récapitulatif complet" },
            { step: 5, text: "Signature du contrat" },
            { step: 6, text: "Validation de la précommande" }
          ].map((item) => (
            <div key={item.step} className="flex items-center gap-3">
              <div className="w-6 h-6 bg-[#F9F6F0] flex items-center justify-center">
                <span className="font-body text-xs font-medium text-[#B89F7C]">{item.step}</span>
              </div>
              <span className="font-body text-sm text-[#0A0A0A]">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Info note */}
      <div className="bg-[#F9F6F0] border border-[#E5E5E5] p-4 mb-8 flex items-start gap-3 text-left">
        <Info size={18} className="text-[#B89F7C] mt-0.5 shrink-0" />
        <p className="font-body text-sm text-[#525252]">
          Après validation de votre précommande, notre équipe vous contactera pour organiser 
          la préparation et le déploiement de votre dispositif au sein de notre réseau partenaire.
        </p>
      </div>

      {/* CTA */}
      <button
        onClick={nextStep}
        data-testid="btn-start-onboarding"
        className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-12 py-4 text-sm tracking-widest uppercase font-medium"
      >
        Commencer ma réservation
      </button>
    </div>
  );
};

export default StepIntro;
